import { useCallback, useLayoutEffect, useRef, useState } from 'react';

import { useIntl } from 'react-intl';

import { useUserData } from 'context';
import { reportError } from 'lib/reportError';
import {
  AUTH_SESSION_CHANGED_EVENT,
  getAuthSessionRevision,
  isAuthSessionCurrent,
} from 'services/auth/session';
import { AUTH_SESSION_EXPIRED_EVENT } from 'services/events';
import { VoidFunctionNoArgs } from 'types';
import { getAxiosErrorMessage } from 'utils';

interface IDialogOperation {
  controller: AbortController;
  owner: number;
  timer: ReturnType<typeof setTimeout> | null;
  showsGlobalLoading: boolean;
}
const LOADING_DELAY_MS = 1000;

export interface IDialogMutation<T> {
  isLoading: boolean;
  errMessage: string;
  resetErrMsg: VoidFunctionNoArgs;
  run: (write: (signal: AbortSignal) => Promise<T>) => Promise<void>;
}

// For keyed dialog actions. Callers own their success publication;
// confirmed actions cannot be submitted again by the same open instance.
// Multistep callers must guard their next steps and own their recovery policy.
export function useDialogMutation<T>(
  onSuccess: (result: T) => void,
  errorContext: string,
  failureMessage?: string | ((error: unknown) => string),
  isActive = true,
): IDialogMutation<T> {
  const { setIsGlobalLoading } = useUserData();
  const { formatMessage } = useIntl();
  const [isLoading, setIsLoading] = useState(false);
  const [errMessage, setErrMessage] = useState('');
  const mounted = useRef(false);
  const revision = useRef(getAuthSessionRevision());
  const operation = useRef<IDialogOperation | null>(null);
  const confirmed = useRef(false);

  const clearLoading = useCallback(
    (pending: IDialogOperation): void => {
      if (pending.timer !== null) clearTimeout(pending.timer);
      pending.timer = null;
      if (pending.showsGlobalLoading) setIsGlobalLoading(false);
      pending.showsGlobalLoading = false;
    },
    [setIsGlobalLoading],
  );

  useLayoutEffect(() => {
    mounted.current = isActive;
    if (!isActive) {
      setIsLoading(false);
      return undefined;
    }
    const discard = (): void => {
      const pending = operation.current;
      if (pending) {
        pending.controller.abort();
        clearLoading(pending);
      }
      operation.current = null;
      if (mounted.current) {
        setIsLoading(false);
        setErrMessage('');
      }
    };
    const onStorage = (event: StorageEvent): void => {
      if (event.storageArea && event.storageArea !== localStorage) return;
      if (!event.key || event.key === 'accessToken' || event.key === 'refreshToken')
        discard();
    };
    window.addEventListener(AUTH_SESSION_CHANGED_EVENT, discard);
    window.addEventListener(AUTH_SESSION_EXPIRED_EVENT, discard);
    window.addEventListener('storage', onStorage);
    return () => {
      mounted.current = false;
      discard();
      window.removeEventListener(AUTH_SESSION_CHANGED_EVENT, discard);
      window.removeEventListener(AUTH_SESSION_EXPIRED_EVENT, discard);
      window.removeEventListener('storage', onStorage);
    };
  }, [clearLoading, isActive]);

  const run = async (write: (signal: AbortSignal) => Promise<T>): Promise<void> => {
    if (
      !mounted.current ||
      operation.current ||
      confirmed.current ||
      !isAuthSessionCurrent(revision.current)
    )
      return;
    const pending: IDialogOperation = {
      controller: new AbortController(),
      owner: revision.current,
      timer: null,
      showsGlobalLoading: false,
    };
    const owns = (): boolean =>
      mounted.current &&
      operation.current === pending &&
      isAuthSessionCurrent(pending.owner);
    operation.current = pending;
    setIsLoading(true);
    setErrMessage('');
    pending.timer = setTimeout(() => {
      if (!owns()) return;
      pending.showsGlobalLoading = true;
      setIsGlobalLoading(true);
    }, LOADING_DELAY_MS);
    try {
      const result = await write(pending.controller.signal);
      if (!owns()) return;
      confirmed.current = true;
      clearLoading(pending);
      onSuccess(result);
    } catch (error) {
      if (owns()) {
        reportError(error, errorContext);
        setErrMessage(
          (typeof failureMessage === 'function'
            ? failureMessage(error)
            : failureMessage) ??
            getAxiosErrorMessage(error, formatMessage({ id: 'server-error.went-wrong' })),
        );
      }
    } finally {
      pending.controller.abort();
      clearLoading(pending);
      if (operation.current === pending) {
        operation.current = null;
        if (mounted.current && !confirmed.current) setIsLoading(false);
      }
    }
  };
  return { isLoading, errMessage, resetErrMsg: () => setErrMessage(''), run };
}
